import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import classNames from 'classnames';
import { ITEDAvenyEKAnalysColumnData } from '../../Queries/EkonomiskAnalysDetailQuery/EkonomiskAnalysDetailQuery';
import EDTable from '../../../../../../../BaseComponents/Table/EDTable';
import { findKey } from '../../Queries/EkonomiskAnalysQueryUtils';
import {
  findFormatterForTEDMixed,
  findFormatter,
} from '../../../../../../../Utils/Formatters/FindFormatter';
import { IFormatter } from '../../../../../../../Utils/Formatters/FormatterTypes';
import { Tab } from '../VerifikationModal/VerifikationDialog';
import { MenuContext } from '../../../../../../MenuContext';
import EDSortableTable, {
  ISortOrder,
} from '../../../../../../../BaseComponents/Table/Components/EDSortableTable';
import { EkonomiskAnalysContextConsumer } from '../Context/EkonomiskAnalysContext';
import Logger from '../../../../../../../Utils/Logger';
import {
  sumDetails,
  filterOutBudgetPerIfNull,
  getInitialSort,
  sumBudgetDetails,
  filterDetails,
} from '../EkonomiskAnalysDetail/Utils/EkonomiskAnalysDetailUtils';
import { numberOrStringCompare } from '../../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import { equals } from 'ramda';
import EDTablePagination from '../../../../../../../BaseComponents/Table/Components/EDTablePagination';
import { AnvandarInfo } from '../../Const/AnvandarInfo';
import shortid from 'shortid';
import { EkonomiskAnalysSaveDBSettingType } from '../../Queries/EkonomiskAnalysSaveDBSettingMutation/EkonomiskAnalysSaveDBSettingMutation';
import EkonomiskAnalysDetailTableHead from './Components/EkonomiskAnalysDetailTableHead';
import EkonomiskAnalysDetailTableBody from './Components/EkonomiskAnalysDetailTableBody';
import {
  BudgetSummationModeType,
  SummationModeType,
} from '../../Utils/SummationModeUtils';

interface IProps {
  columnData: ITEDAvenyEKAnalysColumnData[];
  budgetDetailSummationMode: BudgetSummationModeType;
  detailSummationMode: SummationModeType;
  openVerifikationDialog: (
    id: string | undefined,
    verifikationNr: string,
    bokfdatum: string | undefined,
    tab?: Tab
  ) => void;
  isBudgetColumn: boolean;
  canViewFullVerifikationRader: boolean;
  lopsaldoDetaljrad: boolean;
  ingaendeVardeLopSaldo: number | null;
  saveDBSetting: EkonomiskAnalysSaveDBSettingType;
  defaultRowsPerPage?: number | null;
  visaSummeringar?: boolean;
  classes?: any;
}

interface IState {
  verifikationId: string | undefined; // undefined if VerifikationModal is closed
  openVerifikationTab: Tab | undefined;
  scrollParent?: HTMLDivElement;
  page: number;
  rowsPerPage: number;
}

const styles = (theme: Theme) => ({
  detailsContainer: {
    position: 'relative',
    zIndex: 1,
    padding: '4px 0 4px',
    backgroundColor: theme.palette.background.paper,
  },
  borderBottom: {
    paddingBottom: '14px',
    borderBottom:
      '1px solid ' + theme.customProperties.EkonomiskAnalysTableBorderColor,
  },
  cell: {
    padding: '4px 12px',
  },
  headerCell: {
    paddingBottom: '10px',
  },
  compactCell: {
    width: 12,
    paddingRight: 8,
    paddingLeft: 0,
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  fakturaBildCell: {
    paddingBottom: '2px',
    paddingTop: '2px',
  },
  verNrCell: {
    paddingRight: 16,
  },
  linkCell: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  rightAlignText: {
    textAlign: 'end',
  },
  leftAlignText: {
    textAlign: 'start',
  },
  fakturabildButton: {
    color: theme.customProperties.NeutralColors.mediumGreyForIconButtons,
  },
  scrollContainer: {
    position: 'sticky',
    display: 'flex',
    transition: theme.transitions.create('left', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  scrollInnerContainer: {
    flex: '1',
    width: '100%',
  },
  detailsTable: {
    width: 'auto',
  },
  definitivbokfordWarning: {
    display: 'inline-block',
    backgroundColor: theme.customProperties.Warning.main,
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  notDefinitivRowBg: {
    backgroundColor: theme.palette.grey['100'],
  },
  notDefinitivRow: {
    fontWeight: 600,
    color: theme.customProperties.Warning.dark,
  },
  detailRow: {
    height: '39px',
  },
  overlayRoot: {
    display: 'inline',
  },
});

export interface IDetailCell {
  id: string;
  value: string | null;
  fieldType: FieldType;
  icon: boolean;
}

export interface IDetailRow {
  id: string;
  verifikationId: string;
  verifikationNr: string;
  journalNr: string;
  bokfDatum: string;
  cells: IDetailCell[];
  definitivbokford: boolean;
}

export interface IColumnHeader {
  id: string;
  title1: string;
  title2: string | null;
  title3: string | null;
  formatter: IFormatter;
  isRightAligned: boolean;
  isVisible: boolean;
}

interface IDetailTableHeader {
  columnHeaders: IColumnHeader[];
}

export interface IAnalysDetailTableData {
  tableHeader: IDetailTableHeader;
  tableData: IDetailRow[];
  hasPreliminaryData: boolean;
}

export enum FieldType {
  column,
  verifikation,
  journal,
  definitivbokford,
  debet,
  kredit,
  saldo,
  ackSaldo,
  antal,
  underlag,
  fakturabild,
}

export const ROWS_PER_PAGE_CHOICES = [10, 15, 25, 50, 100, 200, 500];
export const UNCHOSEN_ROWS_PER_PAGE = 25;
export const PAGINATION_DISPLAY_TRESHOLD = 10;

const getColumnHeader = (
  columnId: string,
  columnData: ITEDAvenyEKAnalysColumnData
): IColumnHeader => {
  let title1: string;
  let visible = columnData.visible;

  if (columnData.rubrik1 == null) {
    Logger.error('rubrik1 is null');
    title1 = 'Hittar inte rubrik';
  } else if (columnData.rubrik1 === 'Definitivbokförd') {
    title1 = columnData.rubrik1;
    visible = true;
  } else {
    title1 = columnData.rubrik1;
  }

  return {
    id: columnId,
    isVisible: visible === null ? true : visible,
    title1,
    title2: columnData.rubrik2,
    title3: columnData.rubrik3,
    formatter: findFormatterForTEDMixed(columnData.data),
    isRightAligned: columnData.data
      ? columnData.data.__typename === 'TEDMixedCurrency' ||
        columnData.data.__typename === 'TEDMixedCurrencyArray'
        ? true
        : false
      : false,
  };
};

const convertTEDColumnDataToEkonomiskAnalysDetailTableData = (
  columnDataArray: ITEDAvenyEKAnalysColumnData[],
  lopsaldoDetaljrad: boolean
): IAnalysDetailTableData => {
  const columnHeaders: IColumnHeader[] = [];
  const rows: IDetailRow[] = [];
  let hasPreliminaryData = false;

  for (let i = 0; i < columnDataArray.length; i++) {
    const columnData = columnDataArray[i];
    let id: string;

    if (columnData.fieldname == null) {
      Logger.error('fieldname is null');
      id = 'FieldName is null';
    } else {
      id = columnData.fieldname;
    }

    columnHeaders[i] = getColumnHeader(id, columnData);

    if (columnData.data != null) {
      const key = findKey(columnData.data as any);

      if (key != null) {
        const dataRecord = columnData.data as unknown as Record<string, unknown[]>;
        for (let j = 0; j < dataRecord[key].length; j++) {
          let element = String(
            dataRecord[key][j] != null ? dataRecord[key][j] : ''
          );
          let fieldType = FieldType.column;
          let icon = false;

          if (columnData.fieldtype === 'vernr') {
            fieldType = FieldType.verifikation;
          }

          if (columnData.fieldtype === 'fakturabild') {
            icon = true;
            fieldType = FieldType.fakturabild;
          }

          if (columnData.fieldtype === 'bokfunderlag') {
            icon = true;
            fieldType = FieldType.underlag;
          }

          if (columnData.fieldtype === 'journalnr') {
            fieldType = FieldType.journal;
          }

          if (columnData.fieldtype === 'debet') {
            fieldType = FieldType.debet;
          }
          if (columnData.fieldtype === 'kredit') {
            fieldType = FieldType.kredit;
          }
          if (columnData.fieldtype === 'saldo') {
            fieldType = FieldType.saldo;
          }
          if (columnData.fieldtype === 'antal') {
            fieldType = FieldType.antal;
          }

          if (rows[j] == null) {
            rows[j] = {
              id: j.toString(),
              verifikationId: '',
              verifikationNr: '',
              journalNr: '',
              cells: [],
              definitivbokford: true,
              bokfDatum: '',
            };
          }

          if (columnData.fieldtype === 'definitivbokford') {
            fieldType = FieldType.definitivbokford;
            rows[j].definitivbokford = element.toString() === 'true';
            if (rows[j].definitivbokford === false) {
              hasPreliminaryData = true;
            }
          }

          if (columnData.fieldtype === 'id') {
            rows[j].verifikationId = element;
          }
          if (columnData.fieldtype === 'vernr') {
            rows[j].verifikationNr = element;
          }
          if (columnData.fieldtype === 'journalnr') {
            rows[j].journalNr = element;
          }
          if (columnData.fieldtype === 'datum') {
            rows[j].bokfDatum = element;
          }

          rows[j].cells[i] = { id, value: element, fieldType, icon };
        }
      }
    }
  }

  if (lopsaldoDetaljrad) {
    let headersDone = false;
    // Hitta saldo
    for (let row of rows) {
      const saldoIndex = row.cells.findIndex(
        c => c.fieldType === FieldType.saldo
      );
      if (saldoIndex !== -1) {
        // Headers
        if (!headersDone) {
          columnHeaders.splice(saldoIndex + 1, 0, {
            id: 'acksaldo',
            isRightAligned: true,
            title1: 'Ack. saldo',
            title2: null,
            title3: null,
            isVisible: true,
            formatter: findFormatter('TEDFormatCurr'),
          });
          headersDone = true;
        }
        // Kolumn
        row.cells.splice(saldoIndex + 1, 0, {
          fieldType: FieldType.ackSaldo,
          id: 'acksaldo',
          value: '',
          icon: false,
        });
      }
    }
  }

  return {
    tableHeader: { columnHeaders },
    tableData: rows,
    hasPreliminaryData: hasPreliminaryData,
  };
};

class EkonomiskAnalysDetailTable extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      verifikationId: undefined,
      openVerifikationTab: undefined,
      page: 0,
      rowsPerPage: props.defaultRowsPerPage ?? UNCHOSEN_ROWS_PER_PAGE,
    };
  }

  shouldComponentUpdate(nextProps: IProps, nextState: IState) {
    const equalProps = equals(this.props.columnData, nextProps.columnData);
    const equalState = equals(this.state, nextState);
    return !equalProps || !equalState;
  }

  isVerifikationCol = (cell: IDetailCell): boolean =>
    cell.fieldType === FieldType.verifikation;

  isDefinitivbokfordCol = (cell: IDetailCell): boolean =>
    cell.fieldType === FieldType.definitivbokford;

  isVisible = (cellIndex: number, headCells: IColumnHeader[]): boolean =>
    headCells[cellIndex].isVisible;

  isFakturaBildColumn = (cell: IDetailCell) =>
    cell.fieldType === FieldType.fakturabild;
  hasFakturabild = (cell: IDetailCell) => cell.value === 'Y';
  isUnderlagColumn = (cell: IDetailCell) =>
    cell.fieldType === FieldType.underlag;
  hasBokfOrderUnderlag = (cell: IDetailCell) => cell.value === 'Y';

  isAckSaldo = (cell: IDetailCell) => cell.fieldType === FieldType.ackSaldo;

  showVerifikation = (
    verifikationId: string,
    verifikationNr: string,
    bokfdatum: string | undefined,
    tab?: Tab
  ) => {
    this.props.openVerifikationDialog(
      verifikationId,
      verifikationNr,
      bokfdatum,
      tab
    );
  };

  closeVerication = () => {
    this.setState({
      verifikationId: undefined,
      openVerifikationTab: undefined,
    });
  };

  compare = (a: number | string, b: number | string, order: ISortOrder) => {
    return numberOrStringCompare(a, b, order);
  };

  getValue = (a: IDetailRow, columnName: string) => {
    const cell = a.cells.find(cell => cell && cell.id === columnName);
    return cell ? cell.value : null;
  };

  setScrollParent = (scrollParent: HTMLDivElement) => {
    this.setState({ scrollParent: scrollParent });
  };

  onChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    this.setState({ page });
  };

  onChangeRowsPerPage = (event: any) => {
    this.setState(
      {
        page: 0,
        rowsPerPage: parseInt(event.target.value, 10),
      },
      () =>
        this.props.saveDBSetting(
          AnvandarInfo.ekonomiskAnalysDetailTableRowsPerPage.name,
          { id: shortid.generate(), value: event.target.value }
        )
    );
  };

  getColumnData = (isBudgetColumn: boolean): ITEDAvenyEKAnalysColumnData[] => {
    return isBudgetColumn
      ? this.props.columnData
      : filterDetails(this.props.columnData, this.props.detailSummationMode);
  };

  render() {
    const tableData = convertTEDColumnDataToEkonomiskAnalysDetailTableData(
      this.getColumnData(this.props.isBudgetColumn),
      this.props.lopsaldoDetaljrad
    );
    // Never sum details from budget columns, instead filter out per 0 if both debit and kredit is null
    let summedTableData = !this.props.isBudgetColumn
      ? sumDetails(tableData, this.props.detailSummationMode)
      : sumBudgetDetails(
          filterOutBudgetPerIfNull(tableData),
          this.props.budgetDetailSummationMode
        );

    const headCells = summedTableData.tableHeader.columnHeaders;
    const rows: IDetailRow[] = summedTableData.tableData;
    const hasPreliminaryData: boolean = summedTableData.hasPreliminaryData;

    const scrollElement: Element | null =
      document.querySelector('[data-scroll]');
    const scrollValue = scrollElement ? scrollElement.scrollLeft : 0;
    const { classes } = this.props;

    // Används för att beräkna ackumulerat saldo
    let ackSaldo = this.props.ingaendeVardeLopSaldo ?? 0;
    let saldoIndex: number | null = rows[0]?.cells.findIndex(
      c => c?.fieldType === FieldType.saldo
    );
    if (saldoIndex === -1) {
      saldoIndex = null;
    }

    return (
      <React.Fragment>
        <MenuContext.Consumer>
          {({ rightMenuOpen }) => (
            <EkonomiskAnalysContextConsumer>
              {({ leftSidebarOpen, radBaserad }) => (
                <div
                  className={classNames(classes.detailsContainer, {
                    [classes.borderBottom]: !this.props.visaSummeringar,
                  })}
                >
                  <div
                    ref={this.setScrollParent}
                    data-details=""
                    className={classNames(classes.scrollContainer)}
                    style={{ left: scrollValue + (radBaserad ? 0 : 24) }}
                  >
                    <EDSortableTable
                      key={'table'}
                      sortInfo={{
                        rows: rows,
                        properties: getInitialSort(
                          this.props.detailSummationMode,
                          this.props.isBudgetColumn,
                          this.props.lopsaldoDetaljrad,
                          headCells
                        ),
                      }}
                      compareFunction={this.compare}
                      getValue={this.getValue}
                    >
                      {({ sortInfo, createSortHandler }) => (
                        <div className={classes.scrollInnerContainer}>
                          {sortInfo.rows.length >=
                            PAGINATION_DISPLAY_TRESHOLD && (
                            <EDTablePagination
                              rowsPerPageOptions={ROWS_PER_PAGE_CHOICES}
                              onChangePage={this.onChangePage}
                              onChangeRowsPerPage={this.onChangeRowsPerPage}
                              rowCount={sortInfo.rows.length}
                              rowsPerPage={this.state.rowsPerPage}
                              page={this.state.page}
                            />
                          )}
                          <EDTable autoWidth>
                            <EkonomiskAnalysDetailTableHead
                              createSortHandler={createSortHandler}
                              hasPreliminaryData={hasPreliminaryData}
                              headCells={headCells}
                              sortInfo={sortInfo}
                            />
                            <EkonomiskAnalysDetailTableBody
                              ackSaldo={ackSaldo}
                              canViewFullVerifikationRader={
                                this.props.canViewFullVerifikationRader
                              }
                              hasPreliminaryData={hasPreliminaryData}
                              headCells={headCells}
                              ingaendeVardeLopSaldo={
                                this.props.ingaendeVardeLopSaldo
                              }
                              isBudgetColumn={this.props.isBudgetColumn}
                              lopsaldoDetaljrad={this.props.lopsaldoDetaljrad}
                              page={this.state.page}
                              rowsPerPage={this.state.rowsPerPage}
                              saldoIndex={saldoIndex}
                              showVerifikation={this.showVerifikation}
                              sortInfo={sortInfo}
                            />
                          </EDTable>
                          {sortInfo.rows.length >=
                            PAGINATION_DISPLAY_TRESHOLD && (
                            <EDTablePagination
                              rowsPerPageOptions={ROWS_PER_PAGE_CHOICES}
                              onChangePage={this.onChangePage}
                              onChangeRowsPerPage={this.onChangeRowsPerPage}
                              rowCount={sortInfo.rows.length}
                              rowsPerPage={this.state.rowsPerPage}
                              page={this.state.page}
                            />
                          )}
                        </div>
                      )}
                    </EDSortableTable>
                  </div>
                </div>
              )}
            </EkonomiskAnalysContextConsumer>
          )}
        </MenuContext.Consumer>
      </React.Fragment>
    );
  }
}

export default withStyles(EkonomiskAnalysDetailTable, styles);
